import React, { ReactNode, createContext, useContext, useState } from 'react';
import PopoutDrawer from '../../popout-drawer/PopoutDrawer';

type BeforeCloseHandler = () => boolean | void;
type DrawerContent = ReactNode | (() => ReactNode);

type DrawerState = {
  productLineId: string | null;
  content: DrawerContent | null;
  onBeforeClose?: BeforeCloseHandler;
};

type OpenOptions = {
  productLineId: string;
  content: DrawerContent;
  onBeforeClose?: BeforeCloseHandler;
};

export type InstallServiceDrawerContextValue = {
  activeProductLineId: string | null;
  isOpen: boolean;
  openDrawer: (options: OpenOptions) => void;
  closeDrawer: () => void;
  setContent: (productLineId: string, content: DrawerContent) => void;
};

const InstallServiceDrawerContext = createContext<InstallServiceDrawerContextValue | undefined>(undefined);

const initialState: DrawerState = { productLineId: null, content: null };

export const InstallServiceDrawerProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<DrawerState>(initialState);

  const handleClose = () => {
    setState(prev => {
      if (!prev.productLineId) return prev;
      if (prev.onBeforeClose && prev.onBeforeClose() === false) return prev;
      return initialState;
    });
  };

  const openDrawer = ({ productLineId, content, onBeforeClose }: OpenOptions) => {
    setState({ productLineId, content, onBeforeClose });
  };

  const setContent = (productLineId: string, content: DrawerContent) => {
    setState(prev => {
      if (prev.productLineId !== productLineId) return prev;
      return { ...prev, content };
    });
  };

  const contextValue: InstallServiceDrawerContextValue = {
    activeProductLineId: state.productLineId,
    isOpen: !!state.productLineId,
    openDrawer,
    closeDrawer: handleClose,
    setContent,
  };

  const renderContent = (): ReactNode => {
    if (!state.content) return null;
    return typeof state.content === 'function' ? state.content() : state.content;
  };

  return (
    <InstallServiceDrawerContext.Provider value={contextValue}>
      {children}
      <PopoutDrawer anchor="right" title="" isOpen={!!state.productLineId} onClose={() => handleClose()}>
        {renderContent()}
      </PopoutDrawer>
    </InstallServiceDrawerContext.Provider>
  );
};

export const useInstallServiceDrawer = () => useContext(InstallServiceDrawerContext);
